import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Grid, DialogContent, CircularProgress } from '@material-ui/core';

import Snackbar from '../../util/SnackBar';
import api from '../../services/api';
import PDF from '../PDF';
import ModalWarningEmptyProducts from './ModalWarningEmptyProducts';
import ModalAlreadyHaveAnOrder from './ModalAlreadyHaveAnOrder';
import { submitOrder, checkOrder } from './api';

import useUserContext from '../../hooks/useUserContext';
import useConfigContext from '../../hooks/useConfigContext';

import { StyledDialog, Transition } from './styles';
import { groupByDelivery, summarizeDelivery, findEmptyProducts } from './utils';
import CartNavbar from './components/CartNavbar';
import DeliverySection from './components/DeliverySection';
import SummaryPanel from './components/SummaryPanel';
import ShippingStep from './components/ShippingStep';
import FinishStep from './components/FinishStep';

const QUANTITY_SYNC_DELAY = 500;

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const updateSize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return width;
}

export default function ModalCart({
  open,
  handleClose,
  orderProducts,
  orderSelected,
}) {
  const { configState } = useConfigContext();
  const seasonName = configState.season.name || '';
  const DELIVERIES = configState.deliveries;
  const { userState } = useUserContext();
  const { user: profile } = userState;

  const [products, setProducts] = useState(
    orderProducts?.length > 0 ? orderProducts : []
  );
  const [nav, setNav] = useState(orderProducts?.length > 0 ? 'review' : 'ship');
  const [currency, setCurrency] = useState('USD');
  const [loadingUser, setLoadingUser] = useState(true);
  const [user, setUser] = useState(null);
  const [userVersion, setUserVersion] = useState(0);
  const [shipAdd, setShipAdd] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [selectedShipAddress, setSelectedShipAddress] = useState(0);
  const [isShip, setIsShip] = useState(true);
  const [print, setPrint] = useState(false);
  const [show, setShow] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [downloadExcelURL, setDownloadExcelURL] = useState('');
  const [emptyProducts, setEmptyProducts] = useState([]);
  const [openWarningEmptyProducts, setOpenWarningEmptyProducts] = useState(
    false
  );
  const [openOrderAlreadyExist, setOpenOrderAlreadyExist] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const selectedBillingAddress = 0;
  const loading = Boolean(profile) && loadingUser;
  const wid = useWindowWidth();
  const isMobile = wid < 1024;
  const printRef = useRef();

  // Latest values for the debounced cart sync, so its callbacks never hold
  // stale state.
  const latest = useRef({});
  latest.current = { products, user, shipAdd, selectedShipAddress };

  useEffect(() => {
    if (!profile) return undefined;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/newCostumers/${profile.email}`);
        if (!active) return;
        setUser(data);
        const addresses = data?.account?.addresses || [];
        setShipAdd(addresses.filter(address => address.isShippingAddress));
        setBillingAddresses(
          addresses.filter(address => address.isBillingAddress)
        );
        setLoadingUser(false);
      } catch (err) {
        // keep the loading state; the user can retry by reopening the modal
      }
    })();
    return () => {
      active = false;
    };
  }, [profile, userVersion]);

  useEffect(() => {
    if (!open) return;
    if (orderProducts?.length > 0) {
      setProducts(orderProducts);
      setNav('review');
      return;
    }
    (async () => {
      try {
        const response = await api.get('/my/cart');
        setProducts(response.data);
      } catch (err) {
        // ignore: the cart simply stays empty
      }
    })();
  }, [open, orderProducts]);

  const groups = useMemo(() => groupByDelivery(products), [products]);
  const summaries = useMemo(
    () => ({
      d1: summarizeDelivery(groups.d1, currency),
      d2: summarizeDelivery(groups.d2, currency),
      d3: summarizeDelivery(groups.d3, currency),
      d4: summarizeDelivery(groups.d4, currency),
    }),
    [groups, currency]
  );

  const pendingChangesRef = useRef(new Map());
  const syncTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(syncTimerRef.current), []);

  const flushQuantityChanges = useCallback(async () => {
    const changes = Array.from(pendingChangesRef.current.values());
    pendingChangesRef.current.clear();
    if (changes.length === 0) return;
    try {
      await Promise.all(changes.map(change => api.post('/my/cart', change)));
      const { current } = latest;
      if (current.user) {
        await submitOrder({
          cartInfo: current.products,
          userInfo: {
            email: current.user.email,
            sAddress: current.shipAdd[current.selectedShipAddress],
          },
        });
      }
    } catch (err) {
      // ignore: the next quantity change retries the sync
    }
  }, []);

  const handleChangeQuantity = useCallback(
    (style_code, size, quantity) => {
      setProducts(prev =>
        prev.map(product => {
          if (product.style_code !== style_code) return product;
          const cartInfo = product.cartInfo || [];
          const exists = cartInfo.some(item => item.size === size);
          return {
            ...product,
            cartInfo: exists
              ? cartInfo.map(item =>
                  item.size === size ? { ...item, style_code, quantity } : item
                )
              : [...cartInfo, { style_code, size, quantity }],
          };
        })
      );
      setEmptyProducts(prev =>
        prev.includes(style_code)
          ? prev.filter(code => code !== style_code)
          : prev
      );
      setDownloadExcelURL('');

      pendingChangesRef.current.set(`${style_code}|${size}`, {
        style_code,
        size,
        quantity,
      });
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(
        flushQuantityChanges,
        QUANTITY_SYNC_DELAY
      );
    },
    [flushQuantityChanges]
  );

  const handleRemoveProduct = useCallback(async product => {
    try {
      await api.post('/collections', { style_code: product.style_code });
      setProducts(prev =>
        prev.filter(item => item.style_code !== product.style_code)
      );
    } catch (err) {
      // ignore: the product stays in the cart
    }
  }, []);

  const handleCloseModal = () => {
    handleClose();
    setNav('ship');
  };

  const handleExcel = async () => {
    setLoadingOrder(true);
    try {
      const excelUrl = await api.post('/orderExcel', {
        arrayOfProducts: products,
        email: user.email,
      });
      setDownloadExcelURL(excelUrl.data);
    } catch (err) {
      // ignore: the button goes back to its initial state
    }
    setLoadingOrder(false);
  };

  const currentUserInfo = () => ({
    email: user.email,
    sAddress: shipAdd[selectedShipAddress],
  });

  const handleNext = async () => {
    if (loadingOrder) return;
    if (nav === 'ship') {
      if (shipAdd.length === 0) {
        Snackbar.warning('Order must have shipping address');
        return;
      }
      setLoadingOrder(true);
      const existingOrder = await checkOrder({ userInfo: currentUserInfo() });
      if (existingOrder) {
        setCurrentOrder(existingOrder);
        setLoadingOrder(false);
        setOpenOrderAlreadyExist(true);
        return;
      }
      await submitOrder({ cartInfo: products, userInfo: currentUserInfo() });
      setLoadingOrder(false);
      setNav('place');
      return;
    }
    if (nav === 'review') {
      const empty = findEmptyProducts(products);
      if (empty.length > 0) {
        setEmptyProducts(empty);
        setOpenWarningEmptyProducts(true);
        return;
      }
      setLoadingOrder(true);
      await submitOrder({
        cartInfo: products,
        userInfo: currentUserInfo(),
        status: 'Pending',
      });
      setLoadingOrder(false);
      setNav('finish');
      return;
    }
    setNav('review');
  };

  const handleSubmitReview = async () => {
    if (loadingOrder || (orderSelected && orderSelected.status !== 'Notes'))
      return;
    if (shipAdd.length === 0) {
      Snackbar.warning('Order must have shipping address');
      return;
    }
    setLoadingOrder(true);
    await submitOrder({ cartInfo: products, userInfo: currentUserInfo() });
    setLoadingOrder(false);
    setNav('finish');
  };

  const handleCloseOrderAlreadyExist = () => {
    setOpenOrderAlreadyExist(false);
    setCurrentOrder(null);
  };

  const updateExistingOrder = async () => {
    await submitOrder({ cartInfo: products, userInfo: currentUserInfo() });
    setNav('place');
    handleCloseOrderAlreadyExist();
  };

  const renderStep = () => {
    if (loading) {
      return (
        <Grid container justifyContent="center" alignItems="center">
          <CircularProgress style={{ marginTop: 100 }} />
        </Grid>
      );
    }
    if (nav === 'ship') {
      return (
        <ShippingStep
          isMobile={isMobile}
          wid={wid}
          profile={profile}
          isShip={isShip}
          setIsShip={setIsShip}
          shipAdd={shipAdd}
          setShipAdd={setShipAdd}
          billingAddresses={billingAddresses}
          setBillingAddresses={setBillingAddresses}
          selectedShipAddress={selectedShipAddress}
          setSelectedShipAddress={setSelectedShipAddress}
          selectedBillingAddress={selectedBillingAddress}
        />
      );
    }
    if (nav === 'finish') {
      return (
        <FinishStep
          isMobile={isMobile}
          seasonName={seasonName}
          printRef={printRef}
          setPrint={setPrint}
          downloadExcelURL={downloadExcelURL}
          loadingOrder={loadingOrder}
          onGenerateExcel={handleExcel}
          onClose={handleCloseModal}
        />
      );
    }
    const sectionProps = {
      isMobile,
      currency,
      nav,
      emptyProducts,
      onChangeQuantity: handleChangeQuantity,
      onRemove: handleRemoveProduct,
    };
    return (
      <>
        <Grid>
          <DeliverySection
            first
            delivery={DELIVERIES[0]}
            products={groups.d1}
            {...sectionProps}
          />
          <DeliverySection
            delivery={DELIVERIES[1]}
            products={groups.d2}
            {...sectionProps}
          />
          <DeliverySection
            delivery={DELIVERIES[2]}
            products={groups.d3}
            {...sectionProps}
          />
          <DeliverySection
            delivery={null}
            products={groups.d4}
            {...sectionProps}
          />
          <Grid style={{ height: 100 }} />
        </Grid>
        <SummaryPanel
          isMobile={isMobile}
          wid={wid}
          show={show}
          nav={nav}
          currency={currency}
          setCurrency={setCurrency}
          groups={groups}
          summaries={summaries}
          user={user}
          shipAdd={shipAdd}
          selectedShipAddress={selectedShipAddress}
          orderSelected={orderSelected}
          downloadExcelURL={downloadExcelURL}
          loadingOrder={loadingOrder}
          onGenerateExcel={handleExcel}
          seasonName={seasonName}
          onSubmitReview={handleSubmitReview}
          onEditShipping={() => setNav('ship')}
          onRefreshUser={() => setUserVersion(version => version + 1)}
        />
      </>
    );
  };

  return (
    <StyledDialog
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      fullScreen
    >
      <DialogContent
        style={{
          margin: !isMobile && '0px 1% 50px',
          padding: isMobile && '2%',
        }}
      >
        {print ? (
          <Grid container ref={printRef}>
            <PDF
              name={profile?.name}
              d1={groups.d1}
              d2={groups.d2}
              d3={groups.d3}
            />
          </Grid>
        ) : (
          <Grid style={{ width: '100%', height: '100%' }} id="modal">
            <CartNavbar
              isMobile={isMobile}
              profile={profile}
              seasonName={seasonName}
              nav={nav}
              setNav={setNav}
              orderSelected={orderSelected}
              loadingOrder={loadingOrder}
              onNext={handleNext}
              onClose={handleCloseModal}
              show={show}
              setShow={setShow}
            />
            {renderStep()}
          </Grid>
        )}
        <ModalWarningEmptyProducts
          open={openWarningEmptyProducts}
          handleClose={() => setOpenWarningEmptyProducts(false)}
          quantity={emptyProducts.length}
        />
        <ModalAlreadyHaveAnOrder
          open={openOrderAlreadyExist}
          order={currentOrder}
          handleClose={handleCloseOrderAlreadyExist}
          editOrder={() => {
            setProducts(currentOrder?.products || []);
            setNav('place');
          }}
          newOrder={updateExistingOrder}
        />
      </DialogContent>
    </StyledDialog>
  );
}
